import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  SigninDocument,
  type SigninMutation,
  type SigninMutationVariables,
} from "../../generated/graphql";
import { storage } from "../../lib/storage";
import { useAppDispatch } from "../../store";
import { setCredentials } from "../../store/slices/authSlice";

export function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [signinMutation] = useMutation<SigninMutation, SigninMutationVariables>(SigninDocument);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (!username || !password) {
      setErrorMessage("Please provide both your username and password.");
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await signinMutation({
        variables: { input: { username, password } },
      });

      if (data?.signin) {
        storage.setAccessToken(data.signin.accessToken);
        storage.setRefreshToken(data.signin.refreshToken);

        dispatch(
          setCredentials({
            user: data.signin.user,
            accessToken: data.signin.accessToken,
            refreshToken: data.signin.refreshToken,
          })
        );

        navigate({ to: "/media" });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Invalid credentials. Please try again.";
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {errorMessage && (
        <div className="rounded-xl border border-red-100 bg-red-50/80 p-4 text-sm text-red-600 backdrop-blur-sm">
          {errorMessage}
        </div>
      )}

      <div className="space-y-4">
        {/* Username */}
        <div>
          <label htmlFor="username" className="mb-1.5 block text-sm font-semibold text-gray-700">
            Username
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="block w-full rounded-xl border-0 bg-gray-100/50 px-4 py-3.5 text-gray-900 ring-1 ring-inset ring-gray-200 transition-all placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
            placeholder="Enter your username"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-gray-700">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full rounded-xl border-0 bg-gray-100/50 px-4 py-3.5 text-gray-900 ring-1 ring-inset ring-gray-200 transition-all placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
            placeholder="••••••••"
          />
        </div>
      </div>

      {/* Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="mt-6 flex w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-3.5 text-sm font-semibold text-white transition-all hover:bg-gray-800 hover:shadow-lg hover:shadow-gray-900/20 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isLoading && (
          <svg
            aria-hidden="true"
            className="mr-2 h-4 w-4 animate-spin text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {isLoading ? "Signing in..." : "Sign in"}
      </button>
    </form>
  );
}
